import {
  attachClosestEdge,
  Edge,
  extractClosestEdge,
} from "@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import {
  draggable,
  dropTargetForElements,
} from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { preserveOffsetOnSource } from "@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source";
import { setCustomNativeDragPreview } from "@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview";
import { createPortal } from "react-dom";
import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { NavbarIcon } from "../../atoms/navbar/Icon";
import { TableTextWithEllipsis } from "../../molecules/TableTextWithEllipsis";
import {
  getCardData,
  getCardDropTargetData,
  isCardData,
  isDraggingACard,
  isShallowEqual,
} from "./types";

type CardProps = {
  columnId: string;
  card: any;
  onClick: () => void;
  renderCard?: React.ReactNode;
};

type CardState =
  | {
      type: "idle";
    }
  | {
      type: "is-dragging";
    }
  | {
      type: "is-dragging-and-left-self";
    }
  | {
      type: "is-over";
      dragging: DOMRect;
      closestEdge: Edge;
    }
  | {
      type: "preview";
      container: HTMLElement;
      dragging: DOMRect;
    };

const idle: CardState = { type: "idle" };

export const Card = (props: CardProps) => {
  const { columnId, card, onClick, renderCard } = props;
  const theme = useTheme();

  const outerRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<CardState>(idle);

  useEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) {
      return;
    }

    return combine(
      draggable({
        element: inner,
        getInitialData: ({ element }) =>
          getCardData({
            card,
            columnId,
            rect: element.getBoundingClientRect(),
          }),
        onGenerateDragPreview({ nativeSetDragImage, location, source }) {
          const data = source.data;
          if (!isCardData(data)) {
            return false;
          }
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element: inner, input: location.current.input }),
            render({ container }) {
              setState({
                type: "preview",
                container,
                dragging: inner.getBoundingClientRect(),
              });
            },
          });
        },
        onDragStart() {
          setState({ type: "is-dragging" });
        },
        onDrop() {
          setState(idle);
        },
      }),
      dropTargetForElements({
        element: outer,
        getIsSticky: () => true,
        canDrop: isDraggingACard,
        getData: ({ element, input }) => {
          const data = getCardDropTargetData({ card, columnId });
          return attachClosestEdge(data, { element, input, allowedEdges: ["top", "bottom"] });
        },
        onDragEnter({ source, self }) {
          if (!isCardData(source.data)) {
            return;
          }
          if ((source.data.card.ref ?? source.data.card.key) === (card.ref ?? card.key)) {
            return;
          }
          const closestEdge = extractClosestEdge(self.data);
          if (!closestEdge) {
            return;
          }

          setState({ type: "is-over", dragging: source.data.rect, closestEdge });
        },
        onDrag({ source, self }) {
          if (!isCardData(source.data)) {
            return;
          }
          if ((source.data.card.ref ?? source.data.card.key) === (card.ref ?? card.key)) {
            return;
          }
          const closestEdge = extractClosestEdge(self.data);
          if (!closestEdge) {
            return;
          }
          // optimization - Don't update react state if we don't need to.
          const proposed: CardState = { type: "is-over", dragging: source.data.rect, closestEdge };
          setState((current) => {
            if (isShallowEqual(proposed, current)) {
              return current;
            }
            return proposed;
          });
        },
        onDragLeave({ source }) {
          if (!isCardData(source.data)) {
            return;
          }
          if ((source.data.card.ref ?? source.data.card.key) === (card.ref ?? card.key)) {
            setState({ type: "is-dragging-and-left-self" });
            return;
          }
          setState(idle);
        },
        onDrop() {
          setState(idle);
        },
      })
    );
  }, [card, columnId]);

  return (
    <>
      {state.type === "is-over" && state.closestEdge === "top" ? <DragIndicator /> : null}
      <Box ref={outerRef} display="flex" flexDirection="column">
        {renderCard ? (
          <Box ref={innerRef}>{renderCard}</Box>
        ) : (
          <Box
            ref={innerRef}
            display="flex"
            flexDirection="column"
            gap="8px"
            padding="6px"
            border={`1px solid ${theme.palette.custom.primaryBorder}`}
            borderRadius="3px"
          >
            <Box
              width="100%"
              display="flex"
              gap="8px"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box width="90%" display="flex" gap="8px" alignItems="center">
                <Box
                  display="flex"
                  gap="2px"
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  <NavbarIcon
                    variant="three-dots-vertical"
                    sx={{
                      width: "4px",
                      height: "20px",
                      fill: theme.palette.custom.grey[600],
                    }}
                  />
                  <NavbarIcon
                    variant="three-dots-vertical"
                    sx={{
                      width: "4px",
                      height: "20px",
                      fill: theme.palette.custom.grey[600],
                    }}
                  />
                </Box>
                <TableTextWithEllipsis
                  value={card.title}
                  color={
                    card.hidden
                      ? theme.palette.custom.disabledButton
                      : theme.palette.custom.hyperlink
                  }
                  variant="h3"
                />
              </Box>
              {card.type === "object_key" && (
                <Box
                  display="flex"
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick();
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  {!card.hidden ? (
                    <NavbarIcon
                      variant="show"
                      sx={{
                        width: "16px",
                        height: "16px",
                        minHeight: "16px",
                        minWidth: "16px",
                      }}
                    />
                  ) : (
                    <NavbarIcon
                      variant="hide"
                      sx={{
                        width: "16px",
                        height: "16px",
                        minHeight: "16px",
                        minWidth: "16px",
                      }}
                    />
                  )}
                </Box>
              )}
              {card.type === "custom_field" && (
                <Box
                  display="flex"
                  onClick={(event) => {
                    event.stopPropagation();
                    onClick();
                  }}
                  sx={{ cursor: "pointer" }}
                >
                  <NavbarIcon
                    variant="trash-can"
                    color="error"
                    sx={{
                      width: "16px",
                      height: "16px",
                      minHeight: "16px",
                      minWidth: "16px",
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
      {state.type === "is-over" && state.closestEdge === "bottom" ? <DragIndicator /> : null}
      {state.type === "preview"
        ? createPortal(<CardDragPreview card={card} />, state.container)
        : null}
    </>
  );
};

export const CardDragPreview = ({ card }: { card: any }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="8px"
      padding="6px"
      border={`1px solid ${theme.palette.custom.primaryBorder}`}
      borderRadius="3px"
      bgcolor={theme.palette.custom.primaryBackground}
      width="380px"
    >
      <Box display="flex" gap="8px" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="8px" alignItems="center">
          <Box display="flex" gap="2px">
            <NavbarIcon
              variant="three-dots-vertical"
              sx={{
                width: "4px",
                height: "20px",
                fill: theme.palette.custom.grey[600],
              }}
            />
            <NavbarIcon
              variant="three-dots-vertical"
              sx={{
                width: "4px",
                height: "20px",
                fill: theme.palette.custom.grey[600],
              }}
            />
          </Box>
          <TableTextWithEllipsis
            onClick={() => {}}
            value={card.title ?? card.value}
            color={theme.palette.custom.hyperlink}
            variant="h3"
            showUnderline
          />
        </Box>
      </Box>
    </Box>
  );
};

export const DragIndicator = () => {
  const theme = useTheme();
  return <Box height="3px" width="100%" bgcolor={theme.palette.custom.blue[400]}></Box>;
};
